import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getApi } from "../../helpers/auth_helpers";
import {
  mapAssociationToCustomer,
  visibleGst,
  type OrderCustomerOption,
} from "../orders/orderParty";

const PAGE_SIZE = 10;

const toParty = (row: Record<string, unknown>): OrderCustomerOption | null => {
  const option = mapAssociationToCustomer(row as never);
  if (!option) return null;
  if (!option.customerGst) option.customerGst = visibleGst(String(row.GST || ""));
  if (!option.customerPhone) option.customerPhone = String(row.phoneNumber || "").trim();
  return option;
};

export const loadParties = async (q: string, page: number) => {
  const params = new URLSearchParams({
    offset: String(page),
    limit: String(PAGE_SIZE),
    search: q,
  });
  const response: any = await getApi(
    `${process.env.REACT_APP_SERVERURL || ""}/organization/associations?${params}`
  );
  const payload = response?.data;
  const docs = Array.isArray(payload?.docs) ? payload.docs : [];
  if (response?.success === false || !Array.isArray(payload?.docs)) {
    throw new Error("Parties unavailable");
  }
  return {
    parties: docs.map((row: Record<string, unknown>) => toParty(row)).filter(Boolean) as OrderCustomerOption[],
    received: docs.length,
    total: Number(payload?.length ?? 0),
  };
};

export default function SalesPartyPicker({
  disabled,
  selectedName,
  onPick,
  onCreate,
}: {
  disabled?: boolean;
  selectedName: string;
  onPick: (party: OrderCustomerOption) => void;
  onCreate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [parties, setParties] = useState<OrderCustomerOption[]>([]);
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [box, setBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const page = useRef(0);
  const busy = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    if (!open || !anchor.current) return;
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(420, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const gap = 12;
      const height = Math.min(360, Math.max(window.innerHeight - rect.bottom - gap, 220));
      let top = rect.bottom + 4;
      if (top + height > window.innerHeight - gap) top = Math.max(gap, window.innerHeight - gap - height);
      setBox({ top, left, width, height });
    };
    place();
    const onScroll = (event: Event) => {
      if (panel.current?.contains(event.target as Node)) return;
      place();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchor.current?.contains(target) || panel.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const request = generation.current + 1;
    generation.current = request;
    page.current = 0;
    busy.current = true;
    setLoading(true);
    setError("");
    setParties([]);
    const timer = window.setTimeout(() => {
      loadParties(text.trim(), 0)
        .then((result) => {
          if (request !== generation.current) return;
          setParties(result.parties);
          setTotal(result.total);
          page.current = 1;
          setMore(result.received > 0 && PAGE_SIZE < result.total);
        })
        .catch(() => {
          if (request === generation.current) setError("We couldn't load parties. Check your connection and try again.");
        })
        .finally(() => {
          if (request === generation.current) {
            busy.current = false;
            setLoading(false);
          }
        });
    }, text.trim() ? 250 : 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [open, text, reload]);

  const loadMore = () => {
    const el = scroll.current;
    if (!open || !el || !more || loading || error || busy.current) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 80) return;
    const request = generation.current;
    const next = page.current;
    busy.current = true;
    setLoading(true);
    loadParties(text.trim(), next)
      .then((result) => {
        if (request !== generation.current) return;
        setParties((previous) => {
          const seen = new Set(previous.map((party) => party.partyOrganizationId || party.customerName));
          return previous.concat(result.parties.filter((party) => !seen.has(party.partyOrganizationId || party.customerName)));
        });
        setTotal(result.total);
        page.current = next + 1;
        setMore(result.received > 0 && (next + 1) * PAGE_SIZE < result.total);
      })
      .catch(() => {
        if (request === generation.current) setError("We couldn't load parties. Check your connection and try again.");
      })
      .finally(() => {
        busy.current = false;
        setLoading(false);
      });
  };

  useEffect(() => {
    loadMore();
  });

  const choose = (party: OrderCustomerOption) => {
    onPick(party);
    setText("");
    setOpen(false);
  };

  return (
    <div className="sales-party-picker" ref={anchor}>
      <button
        type="button"
        className="sales-party-picker__trigger"
        aria-label={selectedName ? `Party ${selectedName}` : "Select or add a party"}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{selectedName || "Select or add a party"}</span>
      </button>
      {open && box
        ? createPortal(
            <div
              className="sales-party-picker__panel"
              ref={panel}
              role="region"
              aria-label="Party list"
              style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
            >
              <input
                className="sales-party-picker__search"
                aria-label="Search parties"
                placeholder="Search"
                value={text}
                onChange={(event) => setText(event.target.value)}
                autoFocus
              />
              <div className="sales-party-picker__scroll" ref={scroll} onScroll={loadMore}>
                {parties.map((party) => (
                  <button
                    type="button"
                    key={party.partyOrganizationId || party.customerName}
                    className="sales-party-picker__option"
                    onClick={() => choose(party)}
                  >
                    <strong>{party.customerName}</strong>
                    <small>
                      {[party.customerPhone, party.customerGst ? `GSTIN ${party.customerGst}` : ""]
                        .filter(Boolean)
                        .join(" · ") || (party.kind === "business" ? "Business" : "Individual")}
                    </small>
                  </button>
                ))}
                {loading ? <p role="status">Loading parties…</p> : null}
                {error ? (
                  <p role="alert">
                    {error}{" "}
                    <button type="button" onClick={() => setReload((value) => value + 1)}>
                      Retry
                    </button>
                  </p>
                ) : null}
                {!loading && !error && parties.length === 0 ? <p>No matching parties.</p> : null}
                {!loading && !error && total > 0 ? (
                  <p className="sales-party-picker__end">
                    {parties.length} of {total}
                    {!more ? " · No more parties" : ""}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="sales-party-picker__create"
                onClick={() => {
                  setOpen(false);
                  onCreate();
                }}
              >
                New party
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
